<template>
  <!-- Gallery -->
  <div
    class="mb-5 flex w-full flex-col gap-3 lg:sticky lg:top-0 lg:mb-0 lg:h-[635px] lg:flex-row"
  >
    <!-- Desktop gallery -->
    <div
      v-auto-animate
      class="relative hidden w-[13%] shrink-0 flex-col gap-1.5 lg:flex"
    >
      <!-- Active border : shown for static image gallery -->
      <div
        v-if="staticImageGallery && activeBorderHeight > -1"
        :style="`margin-top: ${activeBorderHeight}px`"
        class="absolute aspect-square w-full cursor-pointer rounded-md border-2 border-primary object-contain shadow-sm transition-all duration-150 ease-in-out"
      />

      <div
        v-if="loading"
        class="flex aspect-square w-full items-center justify-center rounded-md border border-primary/20"
      >
        <SpinnerIcon />
      </div>

      <div
        v-else-if="images?.length"
        ref="imageGallery"
        class="flex h-full flex-col gap-1.5"
      >
        <!-- Static image gallery : when 6 or less images -->
        <template v-if="staticImageGallery">
          <ImageGalleryImage
            v-for="(image, index) in images"
            :key="`image_${index}`"
            :image="image"
            :current="image.full === localCurrentImage.full"
            thumb
            @click="localCurrentImage = image"
          />
        </template>

        <!-- Carousel on desktop : when more than 6 images -->
        <Carousel
          v-else
          v-model:model-value="currentImageIndex"
          class="desktop-carousel mt-[36px]"
          v-bind="desktopCarouselConfig"
          @slide-start="onDesktopSlideStart"
        >
          <Slide
            v-for="(image, index) in images"
            :key="`desktop_image_${index}`"
          >
            <ImageGalleryImage
              :image="image"
              :current="image.full === localCurrentImage.full"
              thumb
              @click="localCurrentImage = image"
            />
          </Slide>

          <template #addons>
            <CarouselNavigation />
          </template>
        </Carousel>
      </div>

      <!-- Default placeholder image -->
      <img
        v-else
        :src="placeholderUrl"
        :alt="placeholderUrl"
        class="gallery-image aspect-square w-full cursor-pointer rounded-md object-contain shadow-sm transition duration-200 ease-in-out hover:opacity-100"
      />
    </div>

    <!-- Desktop default image -->
    <div class="relative hidden w-full lg:block">
      <Transition
        enter-from-class="transition duration-300 ease-in-out opacity-0"
        enter-to-class="transition duration-300 ease-in-out opacity-100"
        leave-from-class="transition duration-300 ease-in-out opacity-100"
        leave-to-class="transition duration-300 ease-in-out opacity-0"
      >
        <div
          v-if="loading"
          class="flex h-full w-full items-center justify-center"
        >
          <SpinnerIcon size="h-10 w-10" />
        </div>

        <div v-else-if="localCurrentImage && localCurrentImage.full">
          <ImageGalleryProductImageLabel
            :show="!!productImageLabel"
            :label="productImageLabel"
          />

          <img
            :key="localCurrentImage.full"
            v-lazy="localCurrentImage.full"
            :src="localCurrentImage.full"
            :alt="localCurrentImage.caption"
            class="m-auto h-full w-full rounded-md object-contain lg:absolute lg:border lg:border-gray-200 lg:p-2"
          />
        </div>

        <!-- Default placeholder image -->
        <img
          v-else
          :src="placeholderUrl"
          :alt="placeholderUrl"
          class="m-auto w-full rounded-md object-contain lg:absolute lg:border lg:border-gray-200 lg:p-2"
        />
      </Transition>
    </div>

    <div v-auto-animate class="relative max-w-full overflow-x-hidden lg:hidden">
      <ImageGalleryProductImageLabel
        :show="!!productImageLabel"
        :label="productImageLabel"
      />

      <!-- Carousel on mobile -->
      <Carousel v-bind="carouselConfig">
        <Slide v-for="(image, index) in images" :key="`image_${index}`">
          <img
            :key="image.full"
            v-lazy="image.full"
            :src="image.full"
            :alt="image.caption"
            class="m-auto h-full w-full cursor-pointer rounded-md object-contain lg:absolute lg:border lg:border-gray-200 lg:p-2"
          />
        </Slide>

        <template #addons>
          <CarouselNavigation />
        </template>
      </Carousel>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  type ComputedRef,
  type PropType,
  ref,
  type Ref,
  type ShallowRef,
  useTemplateRef,
  watch,
} from 'vue';
import _ from 'lodash';
import SpinnerIcon from '@/components/Icons/SpinnerIcon.vue';
import ImageGalleryImage from '@/pages/ProductPage/partials/ProductDetailPage/ImageGalleryImage.vue';
import type { ConfigurableCollectionProductImageObject } from '@/types/joostconnector';
import 'vue3-carousel/carousel.css';
import {
  Carousel,
  type CarouselConfig,
  Navigation as CarouselNavigation,
  Slide,
} from 'vue3-carousel';
import ImageGalleryProductImageLabel from '@/pages/ProductPage/partials/ProductDetailPage/ImageGalleryProductImageLabel.vue';

const emits = defineEmits(['update:current-image']);

const props = defineProps({
  images: {
    type: Array as PropType<ConfigurableCollectionProductImageObject[]>,
    required: true,
  },

  currentImage: {
    type: Object as PropType<ConfigurableCollectionProductImageObject>,
    required: true,
  },
});

const carouselConfig = {
  itemsToShow: 1,
  wrapAround: props.images?.length > 4,
};

const desktopCarouselConfig = {
  dir: 'ttb',
  wrapAround: false,
  itemsToShow: 5,
  itemsToScroll: 1,
  snapAlign: 'start',
  height: '553px',
  gap: 5,
} as CarouselConfig;

const staticImageGallery: ComputedRef<boolean> = computed(
  () => !!props.images?.length && props.images.length <= 6
);

const imageGallery: ShallowRef<HTMLDivElement | null> =
  useTemplateRef('imageGallery');

const localCurrentImage = computed({
  get(): ConfigurableCollectionProductImageObject {
    return props.currentImage as ConfigurableCollectionProductImageObject;
  },

  set(image: ConfigurableCollectionProductImageObject): void {
    emits('update:current-image', image);
  },
});

const currentImageIndex: ComputedRef<number> = computed(() => {
  return _.findIndex(props.images, localCurrentImage.value);
});

const loading: Ref<boolean> = ref(false);
const placeholderUrl: ComputedRef<string> = computed(
  () => window.placeholder_url
);
const activeBorderHeight: ComputedRef = computed(() => {
  // Get first image
  const image = imageGallery.value?.children[0] as HTMLImageElement | undefined;

  // If no image, return negative 1 so we can hide the border
  if (!image) {
    return -1;
  }

  // Get the client height plus 6 pixels for the gap
  return (image.clientHeight + 6) * currentImageIndex.value;
});

const productImageLabel: ComputedRef<string> = computed(
  () => window.product_image_label
);

function onDesktopSlideStart(event: {
  currentSlideIndex: number;
  prevSlideIndex: number;
  slidingToIndex: number;
  slidesCount: number;
}): void {
  localCurrentImage.value =
    _.get(props.images, event.slidingToIndex) ?? localCurrentImage.value;
}

watch(
  () => props.images,
  () => {
    loading.value = false;
  }
);
</script>

<style>
.carousel {
  --vc-nav-background: white;
  --vc-nav-color: black;
  --vc-nav-color-hover: rgba(0, 0, 0, 0.5);
  --vc-nav-width: 36px;
  --vc-nav-height: 36px;
}

.carousel__prev {
  border-radius: 0 0.5rem 0.5rem 0;
}

.carousel__next {
  border-radius: 0.5rem 0 0 0.5rem;
}

.desktop-carousel .carousel__prev {
  border-radius: 100%;
  margin-top: -41px;
  color: rgb(var(--theme-colour-tertiary) / var(--tw-bg-opacity));
}

.desktop-carousel .carousel__next {
  border-radius: 100%;
  margin-bottom: -41px;
  color: rgb(var(--theme-colour-tertiary) / var(--tw-bg-opacity));
}

.desktop-carousel .carousel__next--disabled,
.desktop-carousel .carousel__prev--disabled {
  display: none;
}

.desktop-carousel .carousel__slide--active img {
  border: 2px solid rgb(var(--theme-colour-primary) / var(--tw-bg-opacity));
  border-radius: 6px;
}
</style>
